import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Task 5.4 — which Report sections a user wants to see.
 *
 * The report is one surface for several audiences: a brand manager checking
 * pace, a client reading a recap, finance looking at cost per result. Rather
 * than build three reports, each section can be hidden, and the choice sticks
 * so nobody re-hides the same block every visit.
 *
 * Hidden sections are also omitted from print, so "hide, then print" produces
 * exactly the recap the user assembled on screen.
 */
export type ReportSection =
  | 'reach'
  | 'prizes'
  | 'throughput'
  | 'costs'
  | 'topAnswers'
  | 'demographics'
  | 'health';

/** Display order, and the source of truth for what the toggle bar offers. */
export const REPORT_SECTIONS: ReportSection[] = [
  'reach',
  'prizes',
  'throughput',
  'costs',
  'topAnswers',
  'demographics',
  'health',
];

interface ReportSectionsState {
  /** Only hidden ids are stored — a new section ships visible by default. */
  hidden: Record<string, true>;
  toggle: (id: ReportSection) => void;
  isVisible: (id: ReportSection) => boolean;
  /** Vuelve a enseñarlo todo. */
  showAll: () => void;
}

export const useReportSections = create<ReportSectionsState>()(
  persist(
    (set, get) => ({
      hidden: {},
      toggle: (id) =>
        set((s) => {
          const next = { ...s.hidden };
          if (next[id]) delete next[id];
          else next[id] = true;
          return { hidden: next };
        }),
      isVisible: (id) => !get().hidden[id],
      showAll: () => set({ hidden: {} }),
    }),
    { name: 'xotski.reportSections', version: 1 },
  ),
);
